import { setContext } from "@apollo/client/link/context"
import { getMainDefinition } from "@apollo/client/utilities"

const sequences = new Map()
const MAX_SEQUENCES = 1000
let evictedMaxSeq = 0
let memoryInstanceId = null
const INSTANCE_STORAGE_KEY = "sfg20-write-order-instance"
const SEQUENCE_STORAGE_KEY_PREFIX = "sfg20-write-order-sequences:"
const LOCAL_STORAGE_INSTANCE_KEY = "sfg20-write-order-instance-fallback"
const LOCAL_STORAGE_SEQUENCE_KEY = "sfg20-write-order-sequences-fallback"
const WINDOW_NAME_INSTANCE_PREFIX = "sfg20-write-order:"
const WINDOW_NAME_STATE_PREFIX = "sfg20-write-order-state:"

const ID_CANDIDATES = ["id", "_id", "key", "draftId", "linkId", "projectId", "scheduleId", "userId", "tree", "group"]

/**
 * Apollo Link that automatically attaches write-order headers to every
 * mutation so the server-side `withWriteOrderGuard` can reject stale
 * out-of-order writes.  Queries and subscriptions pass through untouched.
 *
 * Headers attached:
 *  - `x-write-order-seq`     monotonic per orderKey
 *  - `x-write-order-key`     derived from operationName + all id-like variables
 *  - `x-write-order-sent-at` ISO timestamp
 */
export const writeOrderLink = setContext((request, previousContext) => {
    const definition = getMainDefinition(request.query)
    if (definition.kind !== "OperationDefinition" || definition.operation !== "mutation") {
        return {}
    }

    const operationName = definition.name?.value || "mutation"
    const variables = request.variables || {}
    const orderKey = deriveOrderKey(operationName, variables)
    const seq = nextSeq(orderKey)
    const sentAt = new Date().toISOString()
    const instance = getWriteOrderInstance()

    return {
        headers: {
            ...(previousContext?.headers ?? {}),
            "x-write-order-seq": String(seq),
            "x-write-order-key": orderKey,
            "x-write-order-sent-at": sentAt,
            instance,
        },
    }
})

export function getCurrentWriteOrderSnapshot(orderKey) {
    if (!orderKey) return null
    const persisted = loadPersistedState()
    hydrateSequencesFromPersisted(persisted)
    const seq = Math.max(sequences.get(orderKey) ?? 0, persisted?.entries?.[orderKey] ?? 0, 0)
    if (!seq) return null
    return {
        instance: getWriteOrderInstance(),
        seq,
    }
}

export function getCurrentWriteOrderSeq(orderKey) {
    return getCurrentWriteOrderSnapshot(orderKey)?.seq ?? 0
}

export function restoreWriteOrderSnapshot(orderKey, snapshot) {
    if (!orderKey || !snapshot) return
    if (snapshot.instance) {
        setWriteOrderInstance(snapshot.instance)
    }
    ensureMinimumWriteOrderSeq(orderKey, snapshot.seq)
}

export function ensureMinimumWriteOrderSeq(orderKey, seq) {
    if (!orderKey) return
    const minSeq = Number(seq)
    if (!Number.isFinite(minSeq) || minSeq <= 0) return
    const persisted = loadPersistedState()
    hydrateSequencesFromPersisted(persisted)
    const current = Math.max(sequences.get(orderKey) ?? 0, persisted?.entries?.[orderKey] ?? 0, 0)
    if (current >= minSeq) return
    sequences.delete(orderKey)
    sequences.set(orderKey, minSeq)
    persistSequenceState()
}

function nextSeq(orderKey) {
    const persisted = loadPersistedState()
    hydrateSequencesFromPersisted(persisted)
    const next =
        Math.max(
            sequences.get(orderKey) ?? 0,
            persisted?.entries?.[orderKey] ?? 0,
            persisted?.evictedMaxSeq ?? 0,
            evictedMaxSeq
        ) + 1
    sequences.delete(orderKey)
    sequences.set(orderKey, next)
    if (sequences.size > MAX_SEQUENCES) {
        const oldest = sequences.keys().next().value
        evictedMaxSeq = Math.max(evictedMaxSeq, sequences.get(oldest))
        sequences.delete(oldest)
    }
    persistSequenceState()
    return next
}

function deriveOrderKey(operationName, variables) {
    const parts = [operationName]
    for (const candidate of ID_CANDIDATES) {
        if (variables[candidate] != null) parts.push(variables[candidate])
    }
    return parts.join(":")
}

function getWriteOrderInstance() {
    if (typeof window === "undefined") return "server"

    try {
        const existing = window.sessionStorage?.getItem(INSTANCE_STORAGE_KEY)
        if (existing) {
            memoryInstanceId = existing
            return existing
        }
    } catch (e) {
        console.warn("writeOrderLink: sessionStorage instance lookup failed", e?.message || e)
    }

    const windowState = loadWindowNameState()
    if (windowState?.instance) {
        memoryInstanceId = windowState.instance
        return windowState.instance
    }

    try {
        const existing = window.localStorage?.getItem(LOCAL_STORAGE_INSTANCE_KEY)
        if (existing) {
            memoryInstanceId = existing
            return existing
        }
    } catch (e) {
        // Fall through to other fallbacks.
    }

    const instance = createInstanceId()

    setWriteOrderInstance(instance)
    memoryInstanceId ??= instance
    return memoryInstanceId
}

function createInstanceId() {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID()
    }
    return `wo-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

function loadPersistedState() {
    if (typeof window === "undefined") return null
    const instance = getWriteOrderInstance()
    try {
        const raw = window.sessionStorage?.getItem(`${SEQUENCE_STORAGE_KEY_PREFIX}${instance}`)
        if (!raw) return null
        const parsed = JSON.parse(raw)
        if (parsed && typeof parsed === "object") return parsed
    } catch (e) {
        console.warn("writeOrderLink: sessionStorage sequence lookup failed", e?.message || e)
    }
    const windowState = loadWindowNameState()
    if (windowState?.instance === instance) {
        return windowState
    }
    try {
        const raw = window.localStorage?.getItem(LOCAL_STORAGE_SEQUENCE_KEY)
        if (!raw) return null
        const parsed = JSON.parse(raw)
        if (parsed?.instance === instance) return parsed
    } catch (e) {
        console.warn("writeOrderLink: localStorage sequence lookup failed", e?.message || e)
    }
    return null
}

function persistSequenceState() {
    if (typeof window === "undefined") return
    const instance = getWriteOrderInstance()
    const entries = Object.fromEntries(sequences)
    const order = [...sequences.keys()]
    try {
        window.sessionStorage?.setItem(
            `${SEQUENCE_STORAGE_KEY_PREFIX}${instance}`,
            JSON.stringify({
                entries,
                order,
                evictedMaxSeq,
            })
        )
        return
    } catch (e) {
        console.warn("writeOrderLink: sessionStorage sequence persistence failed", e?.message || e)
    }

    try {
        persistWindowNameState({
            instance,
            entries,
            order,
            evictedMaxSeq,
        })
        return
    } catch (e) {
        console.warn("writeOrderLink: window.name sequence persistence failed", e?.message || e)
    }

    try {
        window.localStorage?.setItem(
            LOCAL_STORAGE_SEQUENCE_KEY,
            JSON.stringify({
                instance,
                entries,
                order,
                evictedMaxSeq,
            })
        )
    } catch (e) {
        console.warn("writeOrderLink: localStorage sequence persistence failed", e?.message || e)
    }
}

function setWriteOrderInstance(instance) {
    if (!instance || typeof window === "undefined") return
    memoryInstanceId = instance

    try {
        window.sessionStorage?.setItem(INSTANCE_STORAGE_KEY, instance)
    } catch (e) {
        console.warn("writeOrderLink: sessionStorage instance persistence failed", e?.message || e)
    }

    try {
        const currentState = loadWindowNameState()
        persistWindowNameState({
            instance,
            entries: currentState?.entries ?? {},
            order: currentState?.order ?? [],
            evictedMaxSeq: currentState?.evictedMaxSeq ?? 0,
        })
    } catch (e) {
        console.warn("writeOrderLink: window.name instance persistence failed", e?.message || e)
    }

    try {
        window.localStorage?.setItem(LOCAL_STORAGE_INSTANCE_KEY, instance)
    } catch (e) {
        console.warn("writeOrderLink: localStorage instance persistence failed", e?.message || e)
    }
}

function hydrateSequencesFromPersisted(persisted) {
    // Only hydrate when the in-memory map is still empty so we do not
    // overwrite live runtime state; evictedMaxSeq is still folded in below.
    if (!persisted?.entries || sequences.size) return
    for (const [key, value] of Object.entries(persisted.entries)) {
        sequences.set(key, value)
    }
    evictedMaxSeq = Math.max(evictedMaxSeq, persisted.evictedMaxSeq ?? 0)
}

function loadWindowNameState() {
    if (typeof window === "undefined" || typeof window.name !== "string" || !window.name) return null

    if (window.name.startsWith(WINDOW_NAME_STATE_PREFIX)) {
        try {
            const parsed = JSON.parse(decodeURIComponent(window.name.slice(WINDOW_NAME_STATE_PREFIX.length)))
            if (parsed && typeof parsed === "object") return parsed
        } catch (e) {
            return null
        }
    }

    if (window.name.startsWith(WINDOW_NAME_INSTANCE_PREFIX)) {
        return {
            instance: window.name.slice(WINDOW_NAME_INSTANCE_PREFIX.length),
            entries: {},
            order: [],
            evictedMaxSeq: 0,
        }
    }

    return null
}

function persistWindowNameState(state) {
    if (typeof window === "undefined") return
    window.name = `${WINDOW_NAME_STATE_PREFIX}${encodeURIComponent(JSON.stringify(state))}`
}
